import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, Save, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { METHODOLOGY_FRAMEWORK_OPTIONS } from "@/reports/telecom/operatingContext";

const sb: any = supabase as any;

const REGION_OPTIONS = [
  "East Asia & Pacific",
  "South Asia",
  "Europe & Central Asia",
  "Middle East & North Africa",
  "Sub-Saharan Africa",
  "Latin America & Caribbean",
  "North America",
  "Global / Multi-Region",
];

const SECTOR_OPTIONS = [
  "Telecommunications",
  "Data Center",
  "Energy",
  "Private Equity",
  "Financial Services",
  "Manufacturing",
  "Technology",
  "Other",
];

interface AssessmentProfileFormProps {
  assessmentId: string;
  disabled?: boolean;
}

interface ProfileData {
  client_organization: string;
  parent_organization: string;
  headquarters_location: string;
  region: string;
  sector: string;
  conducted_on_behalf_of: string;
  engagement_start_date: string | null;
  engagement_end_date: string | null;
  primary_contact: string;
  ifc_contact: string; // stored as ifc_contact for backward compat; UI label = "Engagement Contact"
  assessment_conducted_by: string;
  methodology: string;
  methodology_frameworks: string[];
  operating_context_notes: string;
  format: string;
}

const emptyProfile: ProfileData = {
  client_organization: "",
  parent_organization: "",
  headquarters_location: "",
  region: "",
  sector: "",
  conducted_on_behalf_of: "",
  engagement_start_date: null,
  engagement_end_date: null,
  primary_contact: "",
  ifc_contact: "",
  assessment_conducted_by: "",
  methodology: "",
  methodology_frameworks: [],
  operating_context_notes: "",
  format: "",
};

export const AssessmentProfileForm: React.FC<AssessmentProfileFormProps> = ({ assessmentId, disabled }) => {
  const { toast } = useToast();
  const [profile, setProfile] = useState<ProfileData>(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load existing profile
  useEffect(() => {
    if (!assessmentId) return;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await sb.rpc("reviewer_get_assessment_profile", {
          p_assessment_id: assessmentId,
        });
        if (error) {
          console.error("Load profile error:", error);
        } else if (data && data.length > 0) {
          const row = data[0];
          setProfile({
            client_organization: row.client_organization ?? "",
            parent_organization: row.parent_organization ?? "",
            headquarters_location: row.headquarters_location ?? "",
            region: row.region ?? "",
            sector: row.sector ?? "",
            conducted_on_behalf_of: row.conducted_on_behalf_of ?? "",
            engagement_start_date: row.engagement_start_date ?? null,
            engagement_end_date: row.engagement_end_date ?? null,
            primary_contact: row.primary_contact ?? "",
            ifc_contact: row.ifc_contact ?? "",
            assessment_conducted_by: row.assessment_conducted_by ?? "",
            methodology: row.methodology ?? "",
            methodology_frameworks: Array.isArray(row.methodology_frameworks) ? row.methodology_frameworks : [],
            operating_context_notes: row.operating_context_notes ?? "",
            format: row.format ?? "",
          });
        }
      } catch (e) {
        console.error("Load profile exception:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [assessmentId]);

  const handleChange = useCallback(<K extends keyof ProfileData>(field: K, value: ProfileData[K]) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleFramework = useCallback((option: string, checked: boolean) => {
    setProfile(prev => {
      const set = new Set(prev.methodology_frameworks);
      if (checked) set.add(option); else set.delete(option);
      // preserve canonical option order
      const next = METHODOLOGY_FRAMEWORK_OPTIONS.filter(o => set.has(o));
      return { ...prev, methodology_frameworks: next };
    });
  }, []);

  const handleSave = async () => {
    if (!assessmentId) return;
    setSaving(true);
    try {
      const { error } = await sb.rpc("reviewer_upsert_assessment_profile", {
        p_assessment_id: assessmentId,
        p_client_organization: profile.client_organization || null,
        p_parent_organization: profile.parent_organization || null,
        p_headquarters_location: profile.headquarters_location || null,
        p_region: profile.region || null,
        p_sector: profile.sector || null,
        p_engagement_start_date: profile.engagement_start_date || null,
        p_engagement_end_date: profile.engagement_end_date || null,
        p_primary_contact: profile.primary_contact || null,
        p_ifc_contact: profile.ifc_contact || null,
        p_assessment_conducted_by: profile.assessment_conducted_by || null,
        p_methodology: profile.methodology || null,
        p_format: profile.format || null,
        p_conducted_on_behalf_of: profile.conducted_on_behalf_of || null,
        p_methodology_frameworks: profile.methodology_frameworks,
        p_operating_context_notes: profile.operating_context_notes || null,
      });
      if (error) throw error;
      toast({ title: "Profile saved", description: "Assessment profile updated successfully." });
    } catch (e: any) {
      console.error("Save profile error:", e);
      toast({ title: "Save failed", description: e.message || "Could not save profile.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading profile…
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Section A: Organization Details */}
      <fieldset className="rounded-lg border bg-card p-5 space-y-4">
        <legend className="text-sm font-semibold uppercase tracking-wide text-muted-foreground px-2">
          Organization Details
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Client Organization">
            <Input
              value={profile.client_organization}
              onChange={e => handleChange("client_organization", e.target.value)}
              placeholder="e.g. Acme Telecom Ltd"
              disabled={disabled}
            />
          </Field>
          <Field label="Parent Organization">
            <Input
              value={profile.parent_organization}
              onChange={e => handleChange("parent_organization", e.target.value)}
              placeholder="e.g. Acme Holdings"
              disabled={disabled}
            />
          </Field>
          <Field label="Headquarters Location">
            <Input
              value={profile.headquarters_location}
              onChange={e => handleChange("headquarters_location", e.target.value)}
              placeholder="e.g. Nairobi, Kenya"
              disabled={disabled}
            />
          </Field>
          <Field label="Region">
            <Select value={profile.region} onValueChange={v => handleChange("region", v)} disabled={disabled}>
              <SelectTrigger>
                <SelectValue placeholder="Select region" />
              </SelectTrigger>
              <SelectContent>
                {REGION_OPTIONS.map(r => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sector">
            <Select value={profile.sector} onValueChange={v => handleChange("sector", v)} disabled={disabled}>
              <SelectTrigger>
                <SelectValue placeholder="Select sector" />
              </SelectTrigger>
              <SelectContent>
                {SECTOR_OPTIONS.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Conducted on Behalf Of">
            <Input
              value={profile.conducted_on_behalf_of}
              onChange={e => handleChange("conducted_on_behalf_of", e.target.value)}
              placeholder="e.g. IFC, World Bank Group, IDB Invest, ABC Private Equity"
              disabled={disabled}
            />
          </Field>
        </div>
      </fieldset>

      {/* Section B: Engagement Details */}
      <fieldset className="rounded-lg border bg-card p-5 space-y-4">
        <legend className="text-sm font-semibold uppercase tracking-wide text-muted-foreground px-2">
          Engagement Details
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Engagement Start Date">
            <DatePicker
              value={profile.engagement_start_date}
              onChange={v => handleChange("engagement_start_date", v)}
              disabled={disabled}
            />
          </Field>
          <Field label="Engagement End Date">
            <DatePicker
              value={profile.engagement_end_date}
              onChange={v => handleChange("engagement_end_date", v)}
              disabled={disabled}
            />
          </Field>
          <Field label="Format">
            <Input
              value={profile.format}
              onChange={e => handleChange("format", e.target.value)}
              placeholder="e.g. Remote / On-site / Hybrid"
              disabled={disabled}
            />
          </Field>
        </div>
      </fieldset>

      {/* Section B2: Methodology & Frameworks */}
      <fieldset className="rounded-lg border bg-card p-5 space-y-4">
        <legend className="text-sm font-semibold uppercase tracking-wide text-muted-foreground px-2">
          Methodology &amp; Frameworks
        </legend>
        <div className="space-y-4">
          <Field label="Assessment Methodology / Frameworks Used">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
              {METHODOLOGY_FRAMEWORK_OPTIONS.map(opt => {
                const checked = profile.methodology_frameworks.includes(opt);
                return (
                  <label
                    key={opt}
                    className="flex items-start gap-2 rounded-md border bg-background p-2 cursor-pointer hover:bg-accent/30 transition-colors"
                  >
                    <Checkbox
                      checked={checked}
                      disabled={disabled}
                      onCheckedChange={(v) => toggleFramework(opt, v === true)}
                      className="mt-0.5"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Select all frameworks that informed this engagement. These drive the Operating &amp; Regulatory Context narrative.
            </p>
          </Field>

          <Field label="Additional Operating / Regulatory Context Notes">
            <Textarea
              value={profile.operating_context_notes}
              onChange={e => handleChange("operating_context_notes", e.target.value)}
              placeholder="Optional. Add jurisdiction-specific obligations, regulator expectations, or contextual notes that should appear in the report."
              disabled={disabled}
              rows={4}
            />
          </Field>

          <Field label="Legacy / Custom Methodology Notes">
            <Textarea
              value={profile.methodology}
              onChange={e => handleChange("methodology", e.target.value)}
              placeholder="Optional. Free-text methodology notes (used only when no frameworks are selected above)."
              disabled={disabled}
              rows={3}
            />
          </Field>
        </div>
      </fieldset>

      {/* Section C: Contacts */}
      <fieldset className="rounded-lg border bg-card p-5 space-y-4">
        <legend className="text-sm font-semibold uppercase tracking-wide text-muted-foreground px-2">
          Contacts
        </legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Primary Contact">
            <Input
              value={profile.primary_contact}
              onChange={e => handleChange("primary_contact", e.target.value)}
              placeholder="Name / title"
              disabled={disabled}
            />
          </Field>
          <Field label="Engagement Contact">
            <Input
              value={profile.ifc_contact}
              onChange={e => handleChange("ifc_contact", e.target.value)}
              placeholder="e.g. IFC investment officer, WBG program lead, PE deal lead"
              disabled={disabled}
            />
          </Field>
          <Field label="Assessment Conducted By">
            <Input
              value={profile.assessment_conducted_by}
              onChange={e => handleChange("assessment_conducted_by", e.target.value)}
              placeholder="Reviewer name(s)"
              disabled={disabled}
            />
          </Field>
        </div>
      </fieldset>

      {/* Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={disabled || saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
          Save Profile
        </Button>
      </div>
    </div>
  );
};

/* ---- helpers ---- */

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">{label}</label>
      {children}
    </div>
  );
}

function DatePicker({ value, onChange, disabled }: { value: string | null; onChange: (v: string | null) => void; disabled?: boolean }) {
  const date = value ? new Date(value + "T00:00:00") : undefined;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-full justify-start text-left font-normal", !date && "text-muted-foreground")}
          disabled={disabled}
        >
          <CalendarIcon className="h-4 w-4 mr-2" />
          {date ? format(date, "PPP") : "Pick a date"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={d => onChange(d ? format(d, "yyyy-MM-dd") : null)}
          initialFocus
          className="p-3 pointer-events-auto"
        />
      </PopoverContent>
    </Popover>
  );
}
